import React from 'react';
import { FormattedMessage } from 'react-intl';
import { observable, action, autorun } from 'mobx';
import { observer } from 'mobx-react';
import { withRouter } from 'react-router';
import { RetryDialog } from 'lib/alerts/dialog';
import computed from 'lib/mobx/computed';
import { Form, Field } from 'lib/form/form';
import { ValueFieldData, RequiredValidator } from 'lib/form/value-field-data';
import Styled from 'styled';
import withDialog from 'lib/alerts/with-dialog';
import FormView from 'lib/form/form-view';

import withStackViewForm from 'four-nets/ui/with-stack-view-form';
import SubmitButtonView from 'four-nets/ui/submit-button/submit-button-view';
import TextInputView from 'four-nets/ui/form/text-input-view';
import FieldWithLabelView from 'four-nets/ui/form/field-with-label-view';
import SelectView from 'four-nets/ui/form/select-view';
import TextAreaView from 'four-nets/ui-v2/form/text-area/text-area';
import defaultStyles from './edit-thread-view.scss';

import formStyles from './form-view.scss';
import textInputStyles from './text-input-view.scss';

export class EditThreadForm extends Form {
    constructor(options) {
        const { title = '', body = '', category = null } = options;
        super({
            ...options,
            field: {
                category: new Field({
                    data: new ValueFieldData({
                        value: category,
                        validators: [new RequiredValidator()],
                    }),
                }),
                title: new Field({
                    data: new ValueFieldData({
                        value: title,
                        validators: [new RequiredValidator()],
                    }),
                }),
                body: new Field({
                    data: new ValueFieldData({
                        value: body,
                        validators: [new RequiredValidator()],
                    }),
                }),
            },
        });
    }
}

export class EditThreadViewModel {
    categoryStore: CategoryStore;
    photoFileStore: PhotoFileStore;

    @observable form: Form;
    @observable loading = false;
    @observable superCategory;
    @observable loading;

    @observable selectedSuperCategory = 'default';
    @observable selectedCategory = 'default';

    disposer: Function;

    constructor(options: { categoryStore: CategoryStore }) {
        const { categoryStore, thread } = options;
        this.categoryStore = categoryStore;
        this.thread = thread;
    }

    handleSubmit = ({ data }) => {
        const { category, title, body } = data;
        return Promise.resolve(this.thread.update(category, title, body));
    };

    @action setupForm = () => {
        const title = this.thread.title;
        const body = this.thread.firstMessage.body;
        const category = this.thread.category;

        const form = new EditThreadForm({
            title,
            body,
            handleSubmit: this.handleSubmit,
        });

        this.form = form;
        if (category) {
            this.setSelectedCategory(category);
        }
        return form;
    };

    @action setSelectedCategory = category => {
        this.setSuperCategory(category.superCategoryName);
        this.selectedSuperCategory = category.superCategoryName;
        this.selectedCategory = category.id;
        this.changeCategory(category.id);
    };

    @computed get superCategories() {
        return this.categoryStore.superCategories;
    }

    @computed get categories() {
        if (this.superCategory) {
            return this.superCategory.categories;
        }
        return [];
    }

    @action setSuperCategory(name) {
        const superCategories = this.categoryStore.superCategories;

        for (let i = 0; i < superCategories.length; i++) {
            if (name === superCategories[i].name) {
                this.superCategory = superCategories[i];
            }
        }
        this.selectedCategory = 'default';
        this.form.field.get('category').data.setValue({ value: null });
    }

    changeCategory(categoryId) {
        this.form.field.get('category').data.setValue({ value: this.categoryStore.data.get(categoryId) });
    }

    loadCategories() {
        if (!this.categoryStore.didLoadAll) {
            this.loading = true;
            return this.categoryStore.loadAll().finally(() => {
                this.loading = false;
                this.setSelectedCategory(this.thread.category);
            });
        }
        return Promise.resolve();
    }
}

@withStackViewForm({
    title: <FormattedMessage id='EDIT_THREAD_FORM_TITLE' />,
})
@withDialog
@Styled(defaultStyles)
@observer
class EditThreadView extends React.Component {
    bodyTextArea: ?TextAreaView;

    setBodyTextArea = (ref: ?TextAreaView) => {
        this.bodyTextArea = ref;
    };

    setSuperCategory = event => {
        this.props.editThreadViewModel.setSuperCategory(event.target.value);
    };

    setCategory = event => {
        this.props.editThreadViewModel.changeCategory(parseInt(event.target.value, 10));
    };

    submit = () => {
        this.props.submit().then(() => {
            const thread = this.props.editThreadViewModel.thread;
            this.props.history.push(thread.url);
        });
    };

    componentDidMount() {
        this.disposer = autorun(() => {
            this.props.setLoading(this.props.editThreadViewModel.loading);
        });
        this.props.editThreadViewModel.loadCategories().catch(err => {
            this.props.showDialog(
                new RetryDialog({
                    message: err.message,
                    retry: this.props.editThreadViewModel.loadCategories.bind(this.props.editThreadViewModel),
                })
            );
        });
    }

    componentWillUnmount() {
        this.disposer();
    }

    render() {
        const { editThreadViewModel, styles, form } = this.props;
        const { valid, dirty } = form;

        return (
            <FormView form={form} submitOnEnter={false} submit={this.submit} styles={formStyles}>
                <div className={styles.inputs}>
                    <div className={styles.superCategory} key='superCategory'>
                        <FieldWithLabelView
                            key={'supercategory'}
                            field={
                                <SelectView
                                    onChange={this.setSuperCategory}
                                    defaultValue={editThreadViewModel.selectedSuperCategory}
                                >
                                    <FormattedMessage id='BAZAAR_CHOOSE'>
                                        {msg => (
                                            <option disabled value='default'>
                                                {msg}
                                            </option>
                                        )}
                                    </FormattedMessage>
                                    {editThreadViewModel.superCategories.map(superCategory => (
                                        <option value={superCategory.name} key={superCategory.name}>
                                            {superCategory.name}
                                        </option>
                                    ))}
                                </SelectView>
                            }
                            label={<FormattedMessage id='BAZAAR_CATEGORY' />}
                            id={'new-thread-super-category'}
                        />
                    </div>
                    <div className={styles.category} key='category'>
                        <FieldWithLabelView
                            field={
                                <SelectView
                                    onChange={this.setCategory}
                                    key={
                                        (editThreadViewModel.superCategory && editThreadViewModel.superCategory.name) ||
                                        'default'
                                    }
                                    field={this.props.form.field.get('category')}
                                    defaultValue={editThreadViewModel.selectedCategory}
                                >
                                    <FormattedMessage id='BAZAAR_CHOOSE'>
                                        {msg => (
                                            <option disabled value='default'>
                                                {msg}
                                            </option>
                                        )}
                                    </FormattedMessage>
                                    {editThreadViewModel.superCategory &&
                                        editThreadViewModel.superCategory.categories.map(category => (
                                            <option value={category.id} key={category.id}>
                                                {category.name}
                                            </option>
                                        ))}
                                </SelectView>
                            }
                            label={<FormattedMessage id='FORUM_THREAD_CATEGORY' />}
                            id={'new-thread-category'}
                        />
                    </div>
                    <div className={styles.title} key='title'>
                        <FieldWithLabelView
                            key={'title'}
                            field={
                                <TextInputView
                                    field={form.field.get('title')}
                                    type='text'
                                    id={`id_title`}
                                    styles={textInputStyles}
                                />
                            }
                            label={<FormattedMessage id='SUBJECT' />}
                            id={'new-thread-title'}
                        />
                    </div>
                    <div className={styles.content} key='content'>
                        <FieldWithLabelView
                            key={'content'}
                            field={<TextAreaView field={form.field.get('body')} type='text' />}
                            label={<FormattedMessage id='FIRST_POST' />}
                            id={'new-thread-body'}
                        />
                    </div>
                    <div className={styles.button}>
                        <SubmitButtonView disabled={!(valid && dirty)} onClick={this.submit}>
                            <span className={styles.buttonLabel}>
                                <FormattedMessage id='EDIT_THREAD_FORM_TITLE' />
                            </span>
                        </SubmitButtonView>
                    </div>
                </div>
            </FormView>
        );
    }
}

export default withRouter(EditThreadView);
